import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Share2, Check } from 'lucide-react';
import { useApp } from '../contexts/AppContext';
import { getPhotoUrl } from '../api';
import SheepIcon from './SheepIcon';

export default function SheepCard({ sheep }) {
  const { t, lang, formatPrice, addToCart, isInCart } = useApp();
  const navigate = useNavigate();
  const inCart   = isInCart(sheep.id);

  const breedLabel = lang === 'ar' ? sheep.breed_ar : sheep.breed_en;
  const photoUrl   = getPhotoUrl(sheep.photo_url);

  const handleShare = (e) => {
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/api/public/share/sheep/${sheep.id}`;
    const label    = breedLabel || sheep.given_name;
    const text     = `${label} - ${formatPrice(sheep.online_price)}\n${shareUrl}`;
    if (navigator.share) {
      navigator.share({ title: label, text, url: shareUrl });
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  const handleCart = (e) => {
    e.stopPropagation();
    if (!inCart) addToCart(sheep);
  };

  const isRtl   = lang === 'ar';
  const badgeSide = isRtl ? { left: 8 } : { right: 8 };
  const shareSide = isRtl ? { right: 8 } : { left: 8 };

  return (
    <article
      className="sheep-card fade-up"
      onClick={() => navigate(`/sheep/${sheep.id}`)}
      role="button"
      tabIndex={0}
      aria-label={`${breedLabel || sheep.given_name} — ${formatPrice(sheep.online_price)}`}
      onKeyDown={e => { if (e.key === 'Enter') navigate(`/sheep/${sheep.id}`); }}
    >
      {/* ── Image area ── */}
      <div
        style={{
          position: 'relative',
          paddingTop: '82%',
          background: 'linear-gradient(135deg, #F5F0EC 0%, #EDE8E3 100%)',
          overflow: 'hidden',
        }}
      >
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={breedLabel || sheep.given_name}
            loading="lazy"
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transition: 'transform 0.35s ease',
            }}
          />
        ) : (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <SheepIcon size={56} color="#7A1010" opacity={0.25} />
          </div>
        )}

        {/* Gradient overlay at bottom */}
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: 48,
            background: 'linear-gradient(to top, rgba(0,0,0,0.18) 0%, transparent 100%)',
            pointerEvents: 'none',
          }}
        />

        {/* New badge */}
        {sheep.is_new && (
          <span
            className="badge badge-brand"
            style={{
              position: 'absolute',
              top: 8,
              ...badgeSide,
              fontSize: 10,
              padding: '3px 8px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
            }}
          >
            {t.newBadge}
          </span>
        )}

        {/* Share button */}
        <button
          onClick={handleShare}
          aria-label={t.share}
          style={{
            position: 'absolute',
            bottom: 8,
            ...shareSide,
            background: 'rgba(255,255,255,0.92)',
            border: 'none',
            borderRadius: '50%',
            width: 32,
            height: 32,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 1px 6px rgba(0,0,0,0.18)',
            transition: 'background var(--ease)',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#fff'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.92)'; }}
        >
          <Share2 size={14} color="#57534E" />
        </button>
      </div>

      {/* ── Info area ── */}
      <div style={{ padding: '10px 12px 12px' }}>
        {/* Breed / name */}
        <div
          style={{
            fontWeight: 700,
            fontSize: 13,
            color: 'var(--text-1)',
            lineHeight: 1.35,
            marginBottom: 8,
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
        >
          {breedLabel || sheep.given_name}
        </div>

        {/* Gender chip */}
        {sheep.gender && (
          <div
            style={{
              fontSize: 11,
              color: 'var(--text-3)',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span>{sheep.gender === 'male' ? '♂' : '♀'}</span>
            <span>{sheep.gender === 'male'
              ? (lang === 'ar' ? 'ذكر' : 'Male')
              : (lang === 'ar' ? 'أنثى' : 'Female')
            }</span>
          </div>
        )}

        {/* Price + Cart row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 6,
          }}
        >
          {/* Price */}
          <span className="price-tag" style={{ fontSize: 12 }}>
            {formatPrice(sheep.online_price)}
          </span>

          {/* Cart button */}
          <button
            onClick={handleCart}
            aria-label={inCart ? t.inCart : t.addToCart}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              background: inCart ? 'var(--success-bg)' : 'var(--brand)',
              color: inCart ? 'var(--success-txt)' : '#fff',
              border: 'none',
              borderRadius: 'var(--r-full)',
              padding: '6px 11px',
              fontSize: 11,
              fontWeight: 600,
              cursor: inCart ? 'default' : 'pointer',
              transition: 'background var(--ease)',
              fontFamily: 'inherit',
              flexShrink: 0,
            }}
          >
            {inCart ? <Check size={12} strokeWidth={2.5} /> : <ShoppingCart size={12} />}
            {inCart ? t.inCart : t.addToCart}
          </button>
        </div>
      </div>
    </article>
  );
}
